import React, { useMemo } from 'react';
import classNames from 'classnames';
import { getLocale } from 'umi';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import type { ICircularProgressProps } from './types';
import './index.scss';

const DEFAULT_SIZE = 102;

const CircularProgress: React.FC<ICircularProgressProps> = ({
  percent,
  size = DEFAULT_SIZE,
  color = '#326ff0',
  successColor = '#69d19c',
  trackColor = '#ebecf1',
  labelCn,
  labelEn,
  innerRadius = '85%',
  outerRadius = '100%',
  className,
  style,
}) => {
  const isEN = getLocale() === LANGUAGE_EN;
  const safePercent = Math.max(0, percent);
  const fillColor = safePercent >= 100 ? successColor : color;
  const displayPercent = `${Math.round(safePercent)}%`;
  const labelText = getName(isEN, labelEn, labelCn)
    || (isEN ? 'Progress' : '完成率');

  const chartData = useMemo(() => {
    if (safePercent <= 0) {
      return [
        { name: 'filled', value: 0 },
        { name: 'remaining', value: 100 },
      ];
    }
    const filled = Math.min(safePercent, 100);
    const remaining = Math.max(100 - filled, 0) || 0.001;
    return [
      { name: 'filled', value: filled },
      { name: 'remaining', value: remaining },
    ];
  }, [safePercent]);

  const setOtherConfigFunc = useMemo(() => {
    // opts 为基础 ECharts 选项，由 Chart 组件以不可控结构注入，这里仅做浅合并
    return (opts: Record<string, unknown>) => ({
      ...opts,
      tooltip: { show: false },
      legend: { show: false },
      graphic: [],
      animation: true,
      color: [fillColor, trackColor],
      series: [
        {
          type: 'pie',
          radius: [innerRadius, outerRadius],
          center: ['50%', '50%'],
          silent: true,
          minAngle: 0,
          label: { show: false },
          labelLine: { show: false },
          emphasis: { disabled: true },
          startAngle: 90,
          data: chartData,
          itemStyle: {
            borderWidth: 0,
            normal: {
              // ECharts 回调：params 形态由 ECharts 运行时决定，此处仅消费 dataIndex
              color: (params: { dataIndex: number; }) => {
                return params.dataIndex === 0 ? fillColor : trackColor;
              },
            },
          },
        },
      ],
    });
  }, [fillColor, trackColor, innerRadius, outerRadius, chartData]);

  return (
    <div
      className={classNames('dbd-circular-progress', className)}
      style={{ width: size, height: size, ...style }}
    >
      <PieChart
        chartData={chartData}
        isShowLegend={false}
        height={size}
        style={{ width: size, height: size }}
        setOtherConfigFunc={setOtherConfigFunc}
      />
      <div className="dbd-circular-progress__label">
        <span className="dbd-circular-progress__value">{displayPercent}</span>
        <span className="dbd-circular-progress__text">{labelText}</span>
      </div>
    </div>
  );
};

export default CircularProgress;
